import { useState, useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  createStatusFilter,
  createAppFilter,
  createNameSearchFilter,
  createDateRangeFilter,
  applyFilters,
  type EnrichedRecord,
} from '../utils/filters';
import { map, pipe, sort } from 'remeda';
import { normalizeInitSessionConfig } from '../../../utils/extractSessionObject';
import { getVideoById } from '@services/mediaManager';
import type { VideoConfigStep } from '@types';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';

type UseSessionFiltersParams = {
  processedData: EnrichedRecord[];
  selectedStatuses: ('in-progress' | 'queued' | 'completed')[];
  appFilter: string | null;
  nameSearch: string;
  dateRange: [Date | null, Date | null];
  page: number;
  pageSize: number;
};

type UseSessionFiltersReturn = {
  filteredData: EnrichedRecord[];
  tableData: EnrichedRecord[];
  debouncedAppFilter: string | null;
  debouncedNameSearch: string;
  debouncedDateRange: [Date | null, Date | null];
  isFiltering: boolean;
};

export const useSessionFilters = ({
  processedData,
  selectedStatuses,
  appFilter,
  nameSearch,
  dateRange,
  page,
  pageSize,
}: UseSessionFiltersParams): UseSessionFiltersReturn => {
  const [debouncedAppFilter, setDebouncedAppFilter] = useState<string | null>(
    null
  );
  const [debouncedNameSearch, setDebouncedNameSearch] = useState<string>('');
  const [debouncedDateRange, setDebouncedDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAppFilter(appFilter), 150);
    return () => clearTimeout(timer);
  }, [appFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedNameSearch(nameSearch), 300);
    return () => clearTimeout(timer);
  }, [nameSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDateRange(dateRange), 300);
    return () => clearTimeout(timer);
  }, [dateRange]);

  const isFiltering =
    appFilter !== debouncedAppFilter ||
    nameSearch !== debouncedNameSearch ||
    dateRange[0] !== debouncedDateRange[0] ||
    dateRange[1] !== debouncedDateRange[1];

  const filteredData = useMemo(() => {
    const statusFilter = createStatusFilter(selectedStatuses);
    const appFilterFn = createAppFilter(debouncedAppFilter);
    const nameSearchFilter = createNameSearchFilter(debouncedNameSearch);
    const dateRangeFilter = createDateRangeFilter(debouncedDateRange);

    const records = applyFilters(
      processedData,
      statusFilter,
      appFilterFn,
      nameSearchFilter,
      dateRangeFilter
    );

    return pipe(
      records,
      sort((a, b) => {
        const creationTimeA = a.created_at
          ? new Date(a.created_at).getTime()
          : Number.NEGATIVE_INFINITY;
        const creationTimeB = b.created_at
          ? new Date(b.created_at).getTime()
          : Number.NEGATIVE_INFINITY;
        return creationTimeB - creationTimeA;
      })
    );
  }, [
    processedData,
    selectedStatuses,
    debouncedAppFilter,
    debouncedNameSearch,
    debouncedDateRange,
  ]);

  // Calcola tutti i videoId dai primi video nelle config
  const videoIdsMap = useMemo(() => {
    const map = new Map<string, number | string>();
    filteredData.forEach(item => {
      const normalizedConfig = normalizeInitSessionConfig(
        item.config as DeviceConfigurationApi['config']
      );
      const videoSteps = normalizedConfig.filter(
        step => 'video' in step && step.video && step.video !== ''
      ) as VideoConfigStep[];
      if (videoSteps.length > 0) {
        const firstVideoId = videoSteps[0].video;
        if (firstVideoId) {
          map.set(String(item.id), Number(firstVideoId));
        }
      }
    });
    return map;
  }, [filteredData]);

  // Estrai tutti i videoId unici
  const uniqueVideoIds = useMemo(() => {
    return Array.from(new Set(videoIdsMap.values()));
  }, [videoIdsMap]);

  // Usa useQueries per ottenere tutti i video
  const videoQueries = useQueries({
    queries: uniqueVideoIds.map(videoId => ({
      queryKey: ['video', videoId],
      queryFn: () => getVideoById(videoId),
      enabled: Boolean(videoId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    })),
  });

  // Crea mappa per accesso rapido ai video
  const videosMap = useMemo(() => {
    const map = new Map<number | string, (typeof videoQueries)[0]['data']>();
    uniqueVideoIds.forEach((videoId, index) => {
      if (videoQueries[index]?.data) {
        map.set(videoId, videoQueries[index].data);
      }
    });
    return map;
  }, [uniqueVideoIds, videoQueries]);

  const tableData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const x = filteredData.slice(startIndex, endIndex);
    return map(x, item => {
      // Normalizza la config
      const normalizedConfig = normalizeInitSessionConfig(
        item as DeviceConfigurationApi['config']
      );

      // Trova tutti i video nella config
      const videoSteps = normalizedConfig.filter(
        step => 'video' in step && step.video && step.video !== ''
      ) as VideoConfigStep[];

      console.log('normalizedConfig', item.config);
      // Trova il primo video
      const firstVideoStep = videoSteps[0];
      const firstVideoId = firstVideoStep?.video
        ? Number(firstVideoStep.video)
        : null;

      const firstVideo =
        firstVideoId != null ? videosMap.get(firstVideoId) : undefined;
      const experienceSource = firstVideo?.category ?? firstVideo?.name ?? null;

      return {
        ...item,
        experienceName: firstVideo?.name ?? 'Video non trovato',
        experienceSource,
      };
    });
  }, [filteredData, page, pageSize, videosMap]);

  return {
    filteredData,
    tableData,
    debouncedAppFilter,
    debouncedNameSearch,
    debouncedDateRange,
    isFiltering,
  };
};
